/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../../locales/en-US/descriptors';
import {
  getEuiCalculationDate,
  isObjectOrArrayAndEmpty
} from '../../../../shared/Utils/Utils';
import useConnectorAndDynamicProps from '../../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import ConnectionError from '../../../../shared/ErrorComponents/ConnectionError';
import withMSLoadingStatus from '../../../../shared/MSStatusWrappers/MSLoadingStatus';
import useMounted from '../../../../shared/useMount/useMount';
import SiteEUIComponent from '../SiteEUIComponent/SiteEUIComponent';
import ISiteEUIHOCProps from './SiteEUIHOCProps';
import {
  apiNoContentStatusCode,
  euiStatus
} from '../../../../shared/Utils/Constants';
import {
  IEuiData,
  IEuiResponse,
  ISiteCard
} from '../../../IEnergyManagementProps';
import withMSErrorStatus from '../../../../shared/MSStatusWrappers/MSErrorStatus';

interface ISiteEUIHOCContent extends ISiteCard {
  data: IEuiData;
  message?: string;
  preferredEuiUnit: string;
}

const siteEUIHOCContent = (props: ISiteEUIHOCContent) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, preferredEuiUnit, onRefresh } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    if (props.message) {
      return (
        <div className="noDataDisplay ms-common-error-container">
          <div className="error-block heading">{props.message}</div>
        </div>
      );
    }
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return (
    <div>
      <SiteEUIComponent data={data} preferredEuiUnit={preferredEuiUnit} />
    </div>
  );
};

const SiteEUIHOCWithLoader = withMSErrorStatus(
  withMSLoadingStatus(siteEUIHOCContent)
);

const SiteEUIHOC = (props: ISiteEUIHOCProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { bearerToken, requestContext, preferredEuiUnit } = props;

  const connectorConfig = {
    name: 'eui-site',
    options: {
      currentDate: getEuiCalculationDate()
    }
  };

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  useEffect(() => {
    if (isMounted) {
      onRefresh();
    }
  }, [requestContext]);

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const EUITabSite = (all as unknown) as IEuiResponse;
  const isDataAvailable = EUITabSite ? true : false;
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IEuiData);
  const [message, setMessage] = useState<string>('');
  const [statusCode, setStatusCode] = useState<number | null>(status);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(
          (EUITabSite &&
            EUITabSite.status === euiStatus.SUCCESS &&
            EUITabSite.data) ||
            ({} as IEuiData)
        );
        setMessage(EUITabSite.message);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);
  return (
    <SiteEUIHOCWithLoader
      loading={isLoading}
      error={errorInfo}
      statusCode={statusCode}
      onRefresh={onRefresh}
      data={data}
      message={message}
      preferredEuiUnit={preferredEuiUnit}
      loadingText={t(d.LOADING)}
      title={t(d.CONNECTIONERRORTITLE)}
      noDataMessage={t(d.NODATAAVAILABLE)}
      connectionErrorMessage={t(d.CONNECTIONERROR)}
      isNoContent={status === apiNoContentStatusCode}
    />
  );
};
export default SiteEUIHOC;
